import { useCallback, useEffect, useMemo, useRef } from 'react';
import { defaultRowDeserializer, isClosed, createOnTableUpdatedHandler } from '@deephaven/jsapi-utils';
import Log from '@deephaven/log';
import { useOnScrollOffsetChangeCallback } from '@deephaven/react-hooks';
import useInitializeViewportData from "./useInitializeViewportData.js";
import useSetPaddedViewportCallback from "./useSetPaddedViewportCallback.js";
import useTableSize from "./useTableSize.js";
import { SCROLL_DEBOUNCE_MS } from "./Constants.js";
var log = Log.module('useViewportData');
/**
 * Sets up state management for windowed Table viewports. Returns a ListData
 * instance for managing items associated with the Table + a `setViewport`
 * callback for changing the current viewport.
 *
 * IMPORTANT: this will create an empty KeyedItem object for every row in the
 * source table. This is intended for "human" sized tables such as those used in
 * admin panels. This is not suitable for "machine" scale with millions+ rows.
 * @param table The Table to viewport.
 * @param itemHeight The height of each item in the viewport.
 * @param scrollDebounce The number of milliseconds to debounce scroll events.
 * @param viewportSize The number of items to display in the viewport.
 * @param viewportPadding The number of items to fetch at start and end of the viewport.
 * @param deserializeRow A function to deserialize a row from the Table.
 * @param reuseItemsOnTableResize If true, existing items will be re-used when
 * @param viewportSubscriptionOptions The viewport subscription options to use. If provided and
 * the table is not a `TreeTable`, the data will be requested using a `TableViewportSubscription`.
 * Rows and columns are filled in when the subscription is created if they are missing.
 * @returns An object for managing Table viewport state.
 */
export function useViewportData(_ref) {
  var table = _ref.table,
    _ref$itemHeight = _ref.itemHeight,
    itemHeight = _ref$itemHeight === void 0 ? 1 : _ref$itemHeight,
    _ref$scrollDebounce = _ref.scrollDebounce,
    scrollDebounce = _ref$scrollDebounce === void 0 ? SCROLL_DEBOUNCE_MS : _ref$scrollDebounce,
    _ref$viewportSize = _ref.viewportSize,
    viewportSize = _ref$viewportSize === void 0 ? 10 : _ref$viewportSize,
    _ref$viewportPadding = _ref.viewportPadding,
    viewportPadding = _ref$viewportPadding === void 0 ? 50 : _ref$viewportPadding,
    _ref$deserializeRow = _ref.deserializeRow,
    deserializeRow = _ref$deserializeRow === void 0 ? defaultRowDeserializer : _ref$deserializeRow,
    _ref$reuseItemsOnTabl = _ref.reuseItemsOnTableResize,
    reuseItemsOnTableResize = _ref$reuseItemsOnTabl === void 0 ? false : _ref$reuseItemsOnTabl,
    _ref$viewportSubscrip = _ref.viewportSubscriptionOptions,
    viewportSubscriptionOptions = _ref$viewportSubscrip === void 0 ? null : _ref$viewportSubscrip;
  var currentViewportFirstRowRef = useRef(0);
  var viewportData = useInitializeViewportData(table, reuseItemsOnTableResize);

  // Store the memoized callback in a ref so that changes to `viewportData`
  // don't invalidate the memoization of `onTableUpdated`. This prevents
  // `useTableListener` from unnecessarily re-subscribing to the same event over
  // and over.
  var onTableUpdatedRef = useRef();
  onTableUpdatedRef.current = useMemo(() => createOnTableUpdatedHandler(viewportData, deserializeRow), [deserializeRow, viewportData]);
  var onTableUpdated = useCallback(event => {
    var _onTableUpdatedRef$cu;
    (_onTableUpdatedRef$cu = onTableUpdatedRef.current) === null || _onTableUpdatedRef$cu === void 0 || _onTableUpdatedRef$cu.call(onTableUpdatedRef, event);
  }, []);
  var setPaddedViewport = useSetPaddedViewportCallback(table, viewportSize, viewportPadding, viewportSubscriptionOptions, onTableUpdated);
  var setViewport = useCallback(firstRow => {
    currentViewportFirstRowRef.current = firstRow;
    if (table && !isClosed(table)) {
      setPaddedViewport(firstRow);
    } else {
      log.debug('setViewport called on closed table.', table);
    }
  }, [table, setPaddedViewport]);
  var applyFiltersAndRefresh = useCallback(filters => {
    if (table && !isClosed(table)) {
      table.applyFilter(filters);
      setViewport(0);
    } else {
      log.debug('applyFiltersAndRefresh called on closed table.', table);
    }
  }, [setViewport, table]);
  var size = useTableSize(table);
  useEffect(() => {
    log.debug('Initializing viewport');

    // Hydrate the viewport with real data. This will fetch data from index
    // 0 to the end of the viewport + padding.
    setViewport(0);
  }, [table, setViewport]);
  useEffect(() => {
    setViewport(currentViewportFirstRowRef.current);
  }, [setViewport, size]);
  var onScroll = useOnScrollOffsetChangeCallback(itemHeight, setViewport, scrollDebounce);
  return useMemo(() => ({
    viewportData,
    size,
    table,
    applyFiltersAndRefresh,
    setViewport,
    onScroll
  }), [applyFiltersAndRefresh, onScroll, setViewport, size, table, viewportData]);
}
export default useViewportData;
//# sourceMappingURL=useViewportData.js.map